import axios, { AxiosHeaders } from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';

import { endAdminSession, ensureAdminAccessToken, refreshAdminSession } from '@/api/adminSession';
import { toApiError } from '@/api/errors';
import { env } from '@/config/env';
import { routePaths } from '@/routes/routeRegistry';
import {
  getAdminAccessToken,
  isAdminAuthenticated,
  useAdminAuthStore,
} from '@/stores/useAdminAuthStore';
import {
  clearStudentSession,
  getStudentAccessToken,
  isStudentAuthenticated,
  setStudentSession,
} from '@/stores/useAuthStore';
import { useToastStore } from '@/stores/useToastStore';
import type { ApiEnvelope, StudentSession } from '@/types/auth';
import { getOrCreateAuthDeviceId } from '@/utils/authDeviceId';

const baseURL = env.apiBaseUrl;

const axiosInstance = axios.create({
  ...(baseURL ? { baseURL } : {}),
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const refreshClient = axios.create({
  ...(baseURL ? { baseURL } : {}),
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

interface RetriableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _adminLoginId?: string;
}

const isAdminApiRequest = (url?: string): boolean => {
  return Boolean(url?.includes('/api/v1/admin/'));
};

const shouldSkipRefresh = (url?: string): boolean => {
  if (!url) return false;

  return (
    url.includes('/api/v1/auth/login') ||
    url.includes('/api/v1/auth/register') ||
    url.includes('/api/v1/auth/refresh') ||
    url.includes('/api/v1/auth/logout') ||
    url.includes('/api/v1/auth/admin/') ||
    url.includes('/api/v1/admin/')
  );
};

const redirectToLogin = (targetPath: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.location.pathname === targetPath) {
    return;
  }

  window.location.replace(targetPath);
};

const showSessionEndedToast = (error: unknown) => {
  const apiError = toApiError(error, '로그인 정보가 확인되지 않아 다시 로그인이 필요합니다.');
  useToastStore.getState().showToast({
    message: apiError.userMessage,
    variant: 'error',
  });
};

const getResponseErrorCode = (error: unknown): string | null => {
  if (!axios.isAxiosError<unknown>(error)) return null;

  const responseData = error.response?.data;
  if (!responseData || typeof responseData !== 'object') {
    return null;
  }

  const code = (responseData as Record<string, unknown>)['code'];
  return typeof code === 'string' && code.trim() ? code.trim() : null;
};

const endStudentSession = (error: unknown) => {
  clearStudentSession();
  showSessionEndedToast(error);
  redirectToLogin(routePaths.login);
};

axiosInstance.interceptors.request.use(async (config: RetriableRequestConfig) => {
  const previousAdminLoginId = useAdminAuthStore.getState().loginId;
  const accessToken = isAdminApiRequest(config.url)
    ? await ensureAdminAccessToken()
    : getStudentAccessToken();
  if (isAdminApiRequest(config.url)) {
    if (previousAdminLoginId !== useAdminAuthStore.getState().loginId) {
      throw new Error('관리자 계정이 변경되어 요청을 중단했습니다.');
    }
    config._adminLoginId = previousAdminLoginId;
    if (!accessToken) config.headers.delete('Authorization');
  }

  if (accessToken) {
    config.headers = AxiosHeaders.from(config.headers);
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!axios.isAxiosError(error)) {
      const normalizedError =
        error instanceof Error ? error : new Error('Request failed', { cause: error });
      return Promise.reject(normalizedError);
    }

    const requestConfig = error.config as RetriableRequestConfig | undefined;
    const statusCode = error.response?.status ?? null;
    const errorCode = getResponseErrorCode(error);

    if (
      statusCode === 401 &&
      requestConfig &&
      isAdminApiRequest(requestConfig.url) &&
      isAdminAuthenticated()
    ) {
      const failedToken = AxiosHeaders.from(requestConfig.headers).get('Authorization');
      const currentToken = getAdminAccessToken();
      if (requestConfig._adminLoginId !== useAdminAuthStore.getState().loginId) {
        return Promise.reject(error);
      }
      if (errorCode === 'AUTH_401_SESSION' || requestConfig._retry === true) {
        if (failedToken === `Bearer ${currentToken}`) endAdminSession();
        return Promise.reject(error);
      }
      requestConfig._retry = true;
      const token =
        typeof failedToken === 'string' ? failedToken.replace(/^Bearer /, '') : currentToken;
      await refreshAdminSession(token);
      if (requestConfig._adminLoginId !== useAdminAuthStore.getState().loginId) {
        return Promise.reject(error);
      }
      return axiosInstance(requestConfig);
    }

    if (
      statusCode === 401 &&
      errorCode === 'AUTH_401_SESSION' &&
      requestConfig &&
      !isAdminApiRequest(requestConfig.url) &&
      isStudentAuthenticated()
    ) {
      endStudentSession(error);
      return Promise.reject(error);
    }

    if (
      statusCode === 401 &&
      requestConfig &&
      requestConfig._retry !== true &&
      !shouldSkipRefresh(requestConfig.url) &&
      isStudentAuthenticated()
    ) {
      requestConfig._retry = true;

      try {
        const refreshResponse = await refreshClient.post<ApiEnvelope<StudentSession>>(
          '/api/v1/auth/refresh',
          undefined,
          {
            headers: {
              'X-Auth-Device-Id': getOrCreateAuthDeviceId(),
            },
          },
        );
        const nextSession = refreshResponse.data.data;
        setStudentSession(nextSession);
        requestConfig.headers = AxiosHeaders.from(requestConfig.headers);
        requestConfig.headers.set('Authorization', `Bearer ${nextSession.accessToken}`);
        return await axiosInstance(requestConfig);
      } catch (refreshError: unknown) {
        endStudentSession(refreshError);
        const normalizedRefreshError =
          refreshError instanceof Error
            ? refreshError
            : new Error('Request failed', { cause: refreshError });
        return Promise.reject(normalizedRefreshError);
      }
    }

    const normalizedError =
      error instanceof Error ? error : new Error('Request failed', { cause: error });
    return Promise.reject(normalizedError);
  },
);

export default axiosInstance;
